import React from 'react';
import {List} from 'immutable';

import {WebflowEnvironments} from '@packages/systems/core-constants';
import {
  withSystem,
  Plugins,
  Atom,
  type ClassesList,
} from '@packages/systems/wfdl';
import {
  iconName,
  qualify,
  pluginName,
  typeName,
} from '@packages/systems/wfdl-Qualified';
import {TListOfElement, TTypeConstructor} from '@packages/systems/wfdl-ast';
import {createIntegratedAtom} from '@packages/utilities/integrated-types';
import {useRuntimeEnvironment} from '@packages/systems/render';
import {
  getChildren,
  getInPojo,
  getTagOrElse,
} from '@packages/utilities/data-utils';

const designerAndRepeaterClasses = (
  emptyState: boolean,
  classes: ClassesList
) => {
  // We want to always render the DynamoEmpty in the DOM so that all the node drag
  // utils work correctly.
  return emptyState ? classes : classes.concat('w-dyn-hide');
};

const DynamoEmpty = Atom.createRender(
  ({data, attributes, classes, generateAttributes, renderChild, context}) => {
    const Tag = getTagOrElse(getInPojo(data, ['tag']), 'div');
    const listItemIds = context.get('listItemIds', List());
    const repeaterSlugPath = context.get('repeaterSlugPath');
    const repeaterItemCount = context.get('repeaterItemsCount', 0);
    const emptyState =
      repeaterSlugPath && repeaterSlugPath.length > 0
        ? repeaterItemCount === 0
        : listItemIds.size === 0;

    // We always want render DynamoEmpty in the Designer, regardless of the emptyState
    // so that the native node is in the DOM for UI tools.
    const runtimeEnvironment = useRuntimeEnvironment();
    const inDesigner = runtimeEnvironment === WebflowEnvironments.DESIGNER;
    const isRepeaterBound = repeaterSlugPath && repeaterSlugPath.length > 0;
    const classesToAdd =
      inDesigner || isRepeaterBound
        ? designerAndRepeaterClasses(emptyState, classes)
        : classes;
    // If the dynamo list is bound to repeater, then we need to render the empty state in the published site
    // so in cases like variant option change and there are no images to show, we display it.
    return emptyState || inDesigner || isRepeaterBound ? (
      <Tag
        {...generateAttributes(attributes, classesToAdd.push('w-dyn-empty'))}
      >
        {getChildren(data).map((child) => renderChild(child))}
      </Tag>
    ) : null;
  }
);

const config = createIntegratedAtom({
  displayName: 'Empty State',
  icon: {
    type: 'Component',
    value: qualify(Plugins.name('Dynamo'), iconName('CollectionBlock')),
  },
  showEmpty: true,
  uiStateOnSelected: 'empty',
  constraints: {
    pinToParent: true,
    descendants: [{is: 'DynamoWrapper', rule: 'Forbid'}],
  },
  render: DynamoEmpty,
  fields: {
    tag: withSystem(
      {
        label: 'Tag',
        private: true,
        defaultValue: 'div',
      },
      TTypeConstructor(qualify(pluginName('Basic'), typeName('TagsDropdown')))
    ),
    children: TListOfElement,
  },
});

export default config;
